import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ImageOff, MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { OptImg } from "@/components/ui/opt-img";
import { EncabezadoPagina } from "@/components/ui/encabezado-pagina";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NoProjectAccess } from "@/components/admin/NoProjectAccess";
import { cn } from "@/lib/utils";

/**
 * Fila de `get_dashboard_proyectos()`: proyectos gestionados por SOZU que el usuario puede ver
 * (el acceso por proyecto se decide en BD con los helpers current_acceso_*; sin acceso asignado
 * no llega ninguno) y su inventario resumido.
 */
interface ProyectoDashboard {
  id: number;
  nombre: string;
  direccion: string | null;
  url_imagen_portada: string | null;
  precio_m2_actual: number | null;
  metraje_promedio: number | null;
  unidades_total: number;
  unidades_disponibles: number;
  unidades_apartadas: number;
  unidades_inventario: number;
  unidades_vendidas: number;
}

type EstadoProyecto = "venta" | "proximamente" | "vendido";

function estadoDe(p: ProyectoDashboard): EstadoProyecto {
  if (p.unidades_disponibles > 0 || p.unidades_apartadas > 0) return "venta";
  if (p.unidades_inventario > 0) return "proximamente";
  return "vendido";
}

const ESTADO: Record<EstadoProyecto, { texto: string; cls: string }> = {
  venta: { texto: "En venta", cls: "bg-emerald-600 text-white hover:bg-emerald-600" },
  proximamente: { texto: "Próximamente", cls: "bg-amber-500 text-white hover:bg-amber-500" },
  vendido: { texto: "Vendido", cls: "bg-slate-600 text-white hover:bg-slate-600" },
};

/** Tramos de la barra de inventario, en el orden en que se pintan. */
const TRAMOS = [
  { clave: "unidades_vendidas", texto: "Vendidas", cls: "bg-primary" },
  { clave: "unidades_apartadas", texto: "Apartadas", cls: "bg-amber-500" },
  { clave: "unidades_disponibles", texto: "Disponibles", cls: "bg-emerald-500" },
  { clave: "unidades_inventario", texto: "Aún no a la venta", cls: "bg-muted-foreground/30" },
] as const;

const moneda = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const entero = new Intl.NumberFormat("es-MX");

const GRID = "grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4";

const Dashboard = () => {
  const { profile } = useAuth();
  const isInmobiliariaRole = profile?.rol_id === 4;

  // Encabezado con el logo de la inmobiliaria (rol 4).
  const { data: inmobiliariaData } = useQuery({
    queryKey: ["dashboard-inmobiliaria-data", profile?.rol_id, profile?.id_persona],
    queryFn: async () => {
      const { data } = await supabase
        .from("personas")
        .select("nombre_legal, nombre_comercial, url_logo")
        .eq("id", profile!.id_persona)
        .single();
      return data;
    },
    enabled: isInmobiliariaRole && !!profile?.id_persona,
  });

  // Una sola llamada: el servidor filtra los proyectos SOZU por el acceso del usuario y cuenta
  // las unidades. Antes el navegador armaba esto con varias consultas y, sin proyectos
  // asignados, mostraba todos.
  const { data: proyectos = [], isLoading, isError } = useQuery({
    queryKey: ["dashboard-proyectos", profile?.email],
    staleTime: 5 * 60 * 1000,
    enabled: !!profile,
    queryFn: async (): Promise<ProyectoDashboard[]> => {
      const { data, error } = await (supabase as any).rpc("get_dashboard_proyectos");
      if (error) throw error;
      return (data ?? []) as ProyectoDashboard[];
    },
  });

  const disponibles = useMemo(() => proyectos.reduce((s, p) => s + p.unidades_disponibles, 0), [proyectos]);

  const nombreInmobiliaria = inmobiliariaData?.nombre_comercial || inmobiliariaData?.nombre_legal;
  const titulo =
    isInmobiliariaRole && nombreInmobiliaria
      ? `Proyectos comercializados por ${nombreInmobiliaria}`
      : "Proyectos gestionados por Sozu";

  return (
    <div className="space-y-6">
      {isInmobiliariaRole && inmobiliariaData && (
        <Card className="border-0 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent shadow-sm">
          <CardContent className="py-5">
            <div className="flex items-center gap-4">
              <Avatar className="h-14 w-14 ring-2 ring-primary/20">
                {inmobiliariaData.url_logo && (
                  <AvatarImage src={inmobiliariaData.url_logo} alt={inmobiliariaData.nombre_legal || ""} />
                )}
                <AvatarFallback className="bg-primary/10 text-lg font-bold text-primary">
                  {nombreInmobiliaria?.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <h2 className="truncate text-xl font-bold">{nombreInmobiliaria}</h2>
                {inmobiliariaData.nombre_comercial &&
                  inmobiliariaData.nombre_legal &&
                  inmobiliariaData.nombre_comercial !== inmobiliariaData.nombre_legal && (
                    <p className="truncate text-sm text-muted-foreground">{inmobiliariaData.nombre_legal}</p>
                  )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* El resumen va en una línea bajo el título: la tarjeta suelta de "Proyectos" rompía la página. */}
      <EncabezadoPagina
        titulo={titulo}
        ayuda="Solo aparecen los proyectos publicados a los que tienes acceso. Las unidades se cuentan con su estatus de disponibilidad actual."
        detalle={
          isLoading
            ? "Cargando proyectos…"
            : proyectos.length
              ? `${entero.format(proyectos.length)} ${proyectos.length === 1 ? "proyecto" : "proyectos"} · ${entero.format(disponibles)} ${disponibles === 1 ? "unidad disponible" : "unidades disponibles"}`
              : undefined
        }
      />

      {isLoading ? (
        <div className={GRID} aria-busy="true" aria-label="Cargando proyectos">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <Skeleton className="aspect-video w-full rounded-none" />
              <CardContent className="space-y-3 p-5">
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-2 w-full" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No se pudieron cargar los proyectos. Recarga la página o inténtalo más tarde.
          </CardContent>
        </Card>
      ) : proyectos.length === 0 ? (
        <NoProjectAccess message="No tienes proyectos asignados. Contacta al administrador para solicitar acceso a los proyectos." />
      ) : (
        <div className={GRID}>
          {proyectos.map((p) => (
            <TarjetaProyecto key={p.id} proyecto={p} />
          ))}
        </div>
      )}
    </div>
  );
};

function TarjetaProyecto({ proyecto: p }: { proyecto: ProyectoDashboard }) {
  const estado = ESTADO[estadoDe(p)];
  const total = p.unidades_total;
  const tramos = TRAMOS.map((t) => ({ ...t, valor: p[t.clave] })).filter((t) => t.valor > 0);

  return (
    <Card className="flex flex-col overflow-hidden transition-shadow duration-200 hover:shadow-md">
      <div className="relative aspect-video w-full bg-muted">
        {p.url_imagen_portada ? (
          <OptImg
            src={p.url_imagen_portada}
            w={640}
            h={360}
            resize="cover"
            alt={`Portada de ${p.nombre}`}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
            <ImageOff className="h-8 w-8" aria-hidden />
            <span className="text-xs">Sin foto de portada</span>
          </div>
        )}
        <Badge className={cn("absolute right-3 top-3 shadow-sm", estado.cls)}>{estado.texto}</Badge>
      </div>

      <CardContent className="flex flex-1 flex-col gap-4 p-5">
        <div className="min-w-0">
          <h3 className="truncate text-lg font-semibold">{p.nombre}</h3>
          <p className="mt-1 flex items-start gap-1 text-sm text-muted-foreground">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span className="line-clamp-2">{p.direccion || "Sin dirección registrada"}</span>
          </p>
        </div>

        {/* Inventario: las disponibles en grande y la barra con cómo se reparte el total. */}
        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-sm text-muted-foreground">Unidades disponibles</p>
            <p className="tabular-nums">
              <span className="text-2xl font-bold text-foreground">{entero.format(p.unidades_disponibles)}</span>
              <span className="ml-1 text-sm text-muted-foreground">de {entero.format(total)}</span>
            </p>
          </div>
          {total > 0 ? (
            <>
              <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
                {tramos.map((t) => (
                  <div key={t.clave} className={t.cls} style={{ width: `${(t.valor / total) * 100}%` }} />
                ))}
              </div>
              <ul className="flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
                {tramos.map((t) => (
                  <li key={t.clave} className="flex items-center gap-1.5">
                    <span className={cn("size-2 rounded-full", t.cls)} aria-hidden />
                    {t.texto} <span className="tabular-nums text-foreground">{entero.format(t.valor)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-[12px] text-muted-foreground">Sin unidades registradas</p>
          )}
        </div>

        <dl className="mt-auto grid grid-cols-2 gap-3 border-t pt-3 text-sm">
          <div>
            <dt className="text-[12px] text-muted-foreground">Costo por m²</dt>
            <dd className="font-semibold tabular-nums text-foreground">
              {p.precio_m2_actual && p.precio_m2_actual > 0 ? moneda.format(p.precio_m2_actual) : "Sin precio"}
            </dd>
          </div>
          <div>
            <dt className="text-[12px] text-muted-foreground">Metraje promedio</dt>
            <dd className="font-semibold tabular-nums text-foreground">
              {p.metraje_promedio && p.metraje_promedio > 0 ? `${Math.round(p.metraje_promedio)} m²` : "Sin metraje"}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

export default Dashboard;
